import React from 'react';
import { Box, Typography, Avatar, IconButton, SxProps } from '@mui/material';
import { Link } from 'react-router-dom';
import { NEW_TAB_LINK_PROPS } from 'framework/utils';
import { TeamsIcon } from 'framework/ui/mui-builder';

export interface PersonTileUIProps {
  displayName: string;
  initials: string;
  avatarSrc?: string;
  position?: string;
  size?: 'small' | 'medium' | 'large';
  showPosition?: boolean;
  showTeamsButton?: boolean;
  withAvatar?: boolean;
  teamsIconSize?: 'small' | 'medium' | 'large';
  onTeamsClick?: () => void;
  onClick?: () => void;
  sx?: any;
  isLink?: boolean;
  isOpenInNewTab?: boolean;
  linkUrl?: string;
  loading?: boolean;
  sxName?: SxProps;
  sxPosition?: SxProps;
}

const SIZE_MAP = {
  small: { button: 24, icon: 13 },
  medium: { button: 36, icon: 26 },
  large: { button: 48, icon: 32 },
};

const PersonTileUI: React.FC<PersonTileUIProps> = ({
  displayName,
  initials,
  avatarSrc,
  position,
  size = 'medium',
  showPosition = true,
  showTeamsButton = false,
  teamsIconSize = 'small',
  onTeamsClick,
  onClick,
  sx = {},
  isLink = false,
  isOpenInNewTab,
  linkUrl,
  loading = false,
  sxPosition,
  sxName,
  withAvatar = true,
}) => {
  const avatarSize = {
    small: 32,
    medium: 40,
    large: 56,
  }[size];

  const { button: buttonSize, icon: iconSize } = SIZE_MAP[teamsIconSize];

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 0,
          borderRadius: 1,
          ...sx,
        }}
      >
        <Avatar
          sx={{
            width: avatarSize,
            height: avatarSize,
            backgroundColor: 'background.default',
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant={size === 'small' ? 'body2' : 'subtitle2'} noWrap>
            Loading...
          </Typography>
          {position && (
            <Typography variant="body2" color="textSecondary" noWrap>
              {position}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        cursor: onClick ? 'pointer' : 'default',
        p: 0,
        borderRadius: 1,
        '&:hover': onClick
          ? {
              backgroundColor: 'action.hover',
            }
          : {},
        ...sx,
      }}
      onClick={onClick}
    >
      {withAvatar && (
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={avatarSrc}
            alt={displayName}
            sx={{
              width: avatarSize,
              height: avatarSize,
              fontSize: size === 'small' ? '0.875rem' : '1rem',
            }}
          >
            {initials}
          </Avatar>
        </Box>
      )}

      <Box flex={1} minWidth={0}>
        {isLink && linkUrl ? (
          <Link
            to={linkUrl}
            {...(isOpenInNewTab && NEW_TAB_LINK_PROPS)}
            style={{ textDecoration: 'none', color: 'inherit' }}
            onClick={e => {
              e.stopPropagation();
            }}
          >
            <Typography
              variant={size === 'small' ? 'body2' : 'subtitle2'}
              color="primary"
              sx={{ '&:hover': { textDecoration: 'underline' }, ...sxName }}
              noWrap
            >
              {displayName}
            </Typography>
          </Link>
        ) : (
          <Typography variant={size === 'small' ? 'body2' : 'subtitle2'} sx={{ ...sxName }} noWrap>
            {displayName}
          </Typography>
        )}

        {showPosition && position && (
          <Typography sx={sxPosition} variant="body2" color="textSecondary" noWrap>
            {position}
          </Typography>
        )}
      </Box>

      {showTeamsButton && onTeamsClick && (
        <IconButton
          onClick={e => {
            e.stopPropagation();
            onTeamsClick();
          }}
          size={size}
          sx={{
            width: buttonSize,
            height: buttonSize,
            padding: '5px',
            mr: '10px',
          }}
        >
          <TeamsIcon sx={{ fontSize: iconSize }} />
        </IconButton>
      )}
    </Box>
  );
};

export default PersonTileUI;
